import { collection, getDocs, doc, setDoc, deleteDoc, query, where, orderBy, serverTimestamp, getDoc } from 'firebase/firestore';
import { db } from '../config/firebase';
import { AdminUser } from '../types/admin';

export class AdminService {
  // Admin User Management
  static async getAdminUsers(): Promise<AdminUser[]> {
    try {
      const adminUsersQuery = query(
        collection(db, 'admin-users'),
        orderBy('createdAt', 'desc')
      );
      
      const snapshot = await getDocs(adminUsersQuery);
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as AdminUser[];
    } catch (error) {
      console.error('Error fetching admin users:', error);
      throw error;
    }
  }

  static async getActiveAdminUsers(): Promise<AdminUser[]> {
    try {
      const adminUsers = await this.getAdminUsers();
      return adminUsers.filter(user => 
        !user.status || user.status === 'active' || user.status === 'accepted'
      );
    } catch (error) {
      console.error('Error fetching active admin users:', error);
      throw error;
    }
  }

  static async getPendingInvitations(): Promise<AdminUser[]> {
    try {
      const adminUsers = await this.getAdminUsers();
      return adminUsers.filter(user => user.status === 'pending');
    } catch (error) {
      console.error('Error fetching pending invitations:', error);
      throw error;
    }
  }

  static async removeAdminUser(userId: string): Promise<void> {
    try {
      await deleteDoc(doc(db, 'admin-users', userId));
    } catch (error) {
      console.error('Error removing admin user:', error);
      throw error;
    }
  }

  static async isAuthorizedAdmin(email: string): Promise<{ isAuthorized: boolean; role: 'super-admin' | 'admin' | null }> {
    try {
      // Check admin-users collection for this email
      const adminUsersQuery = query(
        collection(db, 'admin-users'),
        where('email', '==', email.toLowerCase())
      );
      const adminUsersSnapshot = await getDocs(adminUsersQuery);
      
      if (!adminUsersSnapshot.empty) {
        const adminUser = adminUsersSnapshot.docs[0].data() as AdminUser;
        
        // For existing records without status field, treat them as active
        // For new records, check if status is active, accepted, or pending
        if (!adminUser.status || adminUser.status === 'active' || adminUser.status === 'accepted') {
          return { 
            isAuthorized: true, 
            role: adminUser.role as 'super-admin' | 'admin'
          };
        }
      }

      return { isAuthorized: false, role: null };
    } catch (error) {
      console.error('Error checking admin authorization:', error);
      return { isAuthorized: false, role: null };
    }
  }

  // Admin Invitation Management
  static async createAdminInvitation(email: string, role: 'super-admin' | 'admin', invitedByUid: string): Promise<string> {
    try {
      // Check if user already exists for this email
      const existingQuery = query(
        collection(db, 'admin-users'),
        where('email', '==', email.toLowerCase())
      );
      const existingSnapshot = await getDocs(existingQuery);
      
      if (!existingSnapshot.empty) {
        throw new Error('An admin user already exists for this email address');
      }

      // Get permissions based on role
      const getPermissionsForRole = (role: 'super-admin' | 'admin') => {
        if (role === 'super-admin') {
          return [
            { resource: 'users', actions: ['read', 'write', 'delete'] },
            { resource: 'admin-users', actions: ['read', 'write', 'delete'] },
            { resource: 'sessions', actions: ['read', 'write', 'delete'] },
            { resource: 'analytics', actions: ['read', 'write'] },
            { resource: 'reports', actions: ['read', 'write', 'delete'] },
            { resource: 'config', actions: ['read', 'write'] },
            { resource: 'system-logs', actions: ['read'] }
          ];
        } else {
          return [
            { resource: 'users', actions: ['read', 'write'] },
            { resource: 'sessions', actions: ['read', 'write'] },
            { resource: 'analytics', actions: ['read'] },
            { resource: 'reports', actions: ['read', 'write'] }
          ];
        }
      };

      const adminUserRef = doc(collection(db, 'admin-users'));
      const expiresAt = new Date(Date.now() + 7 * 24 * 60 * 60 * 1000); // 7 days
      
      // Create pending admin user record (without uid field to match existing structure)
      await setDoc(adminUserRef, {
        email: email.toLowerCase(),
        displayName: '', // Will be filled when user accepts invitation
        photoURL: '',
        role,
        permissions: getPermissionsForRole(role),
        invitedBy: invitedByUid,
        invitedAt: serverTimestamp(),
        status: 'pending',
        expiresAt: expiresAt.toISOString(),
        createdAt: serverTimestamp()
      });

      return adminUserRef.id;
    } catch (error) {
      console.error('Error creating admin invitation:', error);
      throw error;
    }
  }

  static async deleteAdminInvitation(userId: string): Promise<void> {
    try {
      await deleteDoc(doc(db, 'admin-users', userId));
    } catch (error) {
      console.error('Error deleting admin invitation:', error);
      throw error;
    }
  }

  static async acceptAdminInvitation(invitationDocId: string, displayName: string, photoURL?: string, uid?: string): Promise<void> {
    try {
      const invitationRef = doc(db, 'admin-users', invitationDocId);
      const existingDoc = await getDoc(invitationRef);

      if (!existingDoc.exists()) {
        throw new Error('Invitation document not found. Please contact support.');
      }

      // Step 1: Update the existing doc (allowed by rules: email matches)
      await setDoc(invitationRef, {
        uid: uid || '',
        displayName: displayName || '',
        photoURL: photoURL || '',
        status: 'accepted',
        acceptedAt: serverTimestamp(),
        lastLogin: serverTimestamp()
      }, { merge: true });

      // Step 2: Re-key to UID-based doc ID (so Firestore rules work for future operations)
      // This is done as a best-effort — if it fails, fetchAdminUser will handle it on next login
      if (uid && invitationDocId !== uid) {
        try {
          const updatedDoc = await getDoc(invitationRef);
          if (updatedDoc.exists()) {
            await setDoc(doc(db, 'admin-users', uid), updatedDoc.data()!);
            await deleteDoc(invitationRef);
          }
        } catch (rekeyError) {
          console.warn('Re-key deferred — will complete on next super-admin login:', rekeyError);
        }
      }
    } catch (error) {
      console.error('Error accepting admin invitation:', error);
      throw error;
    }
  }

  static async findPendingInvitationByEmail(email: string): Promise<AdminUser | null> {
    try {
      const pendingQuery = query(
        collection(db, 'admin-users'),
        where('email', '==', email.toLowerCase()),
        where('status', '==', 'pending')
      );
      const pendingSnapshot = await getDocs(pendingQuery);
      
      if (!pendingSnapshot.empty) {
        const doc = pendingSnapshot.docs[0];
        return {
          id: doc.id,
          ...doc.data()
        } as AdminUser;
      }
      
      return null;
    } catch (error) {
      console.error('Error finding pending invitation:', error);
      return null;
    }
  }

  // Initialize default super admin if none exist
  static async initializeDefaultSuperAdmin(): Promise<void> {
    try {
      const activeAdmins = await this.getActiveAdminUsers();
      
      if (activeAdmins.length === 0) {
        // Create default super admin from environment or hardcoded
        const defaultEmail = import.meta.env.VITE_ADMIN_EMAILS?.split(',')[0] || 'rokibulhasantomal@gmail.com';
        
        // Get permissions for super admin
        const permissions = [
          { resource: 'users', actions: ['read', 'write', 'delete'] },
          { resource: 'admin-users', actions: ['read', 'write', 'delete'] },
          { resource: 'sessions', actions: ['read', 'write', 'delete'] },
          { resource: 'analytics', actions: ['read', 'write'] },
          { resource: 'reports', actions: ['read', 'write', 'delete'] },
          { resource: 'config', actions: ['read', 'write'] },
          { resource: 'system-logs', actions: ['read'] }
        ];

        const adminUserRef = doc(collection(db, 'admin-users'));
        await setDoc(adminUserRef, {
          uid: '', // Will be filled when user first logs in
          email: defaultEmail.toLowerCase(),
          displayName: 'System Admin',
          photoURL: '',
          role: 'super-admin',
          permissions,
          invitedBy: 'system',
          invitedAt: serverTimestamp(),
          status: 'active', // Set as active so they can login immediately
          createdAt: serverTimestamp(),
          lastLogin: ''
        });
        
        console.log('Default super admin initialized:', defaultEmail);
      }
    } catch (error) {
      console.error('Error initializing default super admin:', error);
      throw error;
    }
  }
}